import type { AxiosError } from 'axios';
import { Platform } from 'react-native';
import { createMutation } from 'react-query-kit';

import { client } from '@/api/common';

export type OrganizationAttachment = {
  uri: string;
  name: string;
  type: string;
  file?: File;
};

type Variables = {
  name?: string;
  urlSlug?: string;
  description?: string;
  keywords?: string[];
  darkLogo?: OrganizationAttachment;
  lightLogo?: OrganizationAttachment;
  darkFavicon?: OrganizationAttachment;
  lightFavicon?: OrganizationAttachment;
};

export type CreateOrganizationResponse = {
  data: {
    id: string;
  };
};

const getOrganizationAttachmentFormValue = (
  file: NonNullable<Variables['darkLogo'] | Variables['lightLogo']>
) => {
  if (Platform.OS === 'web') {
    if (!file.file) {
      throw new Error('Web report upload requires a File object.');
    }

    return file.file;
  }

  return {
    uri: file.uri,
    name: file.name,
    type: file.type,
  };
};

export const useCreateOrganization = createMutation<
  CreateOrganizationResponse,
  Variables,
  AxiosError
>({
  mutationFn: async (variables) => {
    const formData = new FormData();

    if (variables.darkLogo) {
      formData.append(
        'dark_logo',
        getOrganizationAttachmentFormValue(variables.darkLogo) as any
      );
    }
    if (variables.lightLogo) {
      formData.append(
        'light_logo',
        getOrganizationAttachmentFormValue(variables.lightLogo) as any
      );
    }
    if (variables.darkFavicon) {
      formData.append(
        'dark_favicon',
        getOrganizationAttachmentFormValue(variables.darkFavicon) as any
      );
    }
    if (variables.lightFavicon) {
      formData.append(
        'light_favicon',
        getOrganizationAttachmentFormValue(variables.lightFavicon) as any
      );
    }
    formData.append('url_slug', variables.urlSlug || '');
    formData.append('name', variables.name || '');
    // formData.append('description', variables.description || '');
    if (variables.keywords) {
      variables.keywords.forEach((keyword) => {
        formData.append('keywords[]', keyword);
      });
    }

    return client({
      url: `v2/internal/partners/organizations`,
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
      .then((response) => response.data)
      .catch((err: any) => {
        throw new Error(err.response.data.errors.join(', '));
      });
  },
});
